/**
 * Resumen de la planilla de Previred, antes de descargarla.
 *
 * El botón de Previred abría una descarga directa, así que el archivo salía sin
 * que nadie supiera qué contenía: el primer control real era el validador del
 * portal —y el 2026-09-01 rechazó la planilla de agosto con 34 errores—. Ahora
 * el botón abre esto, y la descarga vive adentro.
 *
 * 🔴 El total que se muestra **no es el costo del empleador**: incluye lo que se
 * le descontó al trabajador de su líquido, porque a la institución le llega todo
 * junto y quien lo entera es la empresa. Por eso van las tres cifras separadas.
 */
import { useQuery } from '@tanstack/react-query'
import { DataTable } from '@/components/ds/DataTable'
import { AlertTriangle, Download, Lock } from 'lucide-react'

import apiClient, { apiDownload } from '@/lib/apiClient'
import { useMoney } from '@/lib/locale'
import { Modal } from '@/components/ds/Modal'

interface Concepto {
  etiqueta: string
  quien_aporta: 'trabajador' | 'empleador'
  monto: number
}

interface Institucion {
  grupo: string
  etiqueta: string
  codigo: string
  /** El nombre que Previred le da al código: «Capital», «La Araucana». */
  nombre: string
  trabajadores: number
  renta_imponible: number
  total: number
  aporte_trabajador: number
  aporte_empleador: number
  conceptos: Concepto[]
}

/** La planilla congelada al pagar el período (#1747). */
interface Declarado {
  origen: 'clobi' | 'archivo' | 'reconstruido'
  origen_texto: string
  fecha: string
  por: string
  nota: string
}

interface DiferenciaLinea {
  rut: string
  nombre: string
  linea: number
  campo: string
  etiqueta: string
  declarado: string
  actual: string
}

/** Qué cambiaría si el mes se generara hoy. Solo informa: lo declarado no se mueve. */
interface DiferenciaActual {
  total: number
  aporte_trabajador: number
  aporte_empleador: number
  detalle: DiferenciaLinea[]
}

export interface ResumenPrevired {
  declarado: Declarado | null
  diferencia_actual: DiferenciaActual | null
  year: number
  month: number
  lineas_archivo: number
  trabajadores: number
  total: number
  aporte_trabajador: number
  aporte_empleador: number
  /** Lo que impide bajar el archivo: el servidor se niega a generarlo. */
  bloqueos: string[]
  instituciones: Institucion[]
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

export function PreviredResumenModal({ year, month, open, onClose }: {
  year: number
  month: number
  open: boolean
  onClose: () => void
}) {
  const money = useMoney()
  const { data, isLoading, error } = useQuery<ResumenPrevired>({
    queryKey: ['previred-resumen', year, month],
    queryFn: async () =>
      (await apiClient.get(`/payroll/previred/resumen/?year=${year}&month=${month}`)).data,
    enabled: open,
  })

  const descargar = () => apiDownload(
    `/payroll/previred/?year=${year}&month=${month}`,
    // .txt y NO .csv: Previred pide un archivo TXT y en la pantalla de carga se
    // elige «105 Campos por Separador». El delimitador define el formato, la
    // extensión define si el portal lo acepta.
    `previred_${year}_${String(month).padStart(2, '0')}.txt`,
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Previred — ${MESES[month - 1]} ${year}`}
      size="md"
      actions={
        <div className="flex items-center justify-between gap-3 w-full">
          <p className="text-xs text-content-subtle">
            Se sube en Previred → Pagar Cotizaciones → Cargar Nómina, eligiendo
            <strong className="text-content"> 105 Campos por Separador</strong>.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-divider hover:bg-soft">
              Cerrar
            </button>
            <button onClick={descargar} disabled={!data || data.lineas_archivo === 0 || data.bloqueos.length > 0}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover
                         disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
              <Download size={14} /> Descargar archivo
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {isLoading && <p className="text-sm text-content-subtle">Calculando el resumen…</p>}

        {error != null && (
          <p className="text-sm text-danger-content-soft">
            No se pudo calcular el resumen. El archivo se puede descargar igual,
            pero conviene revisar qué pasó antes de subirlo.
          </p>
        )}

        {data?.declarado != null && <AvisoDeclarado declarado={data.declarado} />}

        {data?.diferencia_actual != null && (
          <AvisoDiferencia dif={data.diferencia_actual} money={money} />
        )}

        {data != null && data.bloqueos.length > 0 && (
          <div className="flex gap-2 items-start p-3 rounded-lg bg-danger-surface">
            <AlertTriangle size={16} className="text-danger-content-soft mt-0.5 shrink-0" />
            <div className="text-sm text-content space-y-1">
              <p>El archivo no se puede descargar hasta corregir lo siguiente:</p>
              <ul className="list-disc pl-5">
                {data.bloqueos.map(b => <li key={b}>{b}</li>)}
              </ul>
            </div>
          </div>
        )}

        {data != null && data.lineas_archivo === 0 && (
          <div className="flex gap-2 items-start p-3 rounded-lg bg-warning-surface">
            <AlertTriangle size={16} className="text-warning-content-soft mt-0.5 shrink-0" />
            <p className="text-sm text-content">
              El período no tiene liquidaciones aprobadas o pagadas, así que el
              archivo saldría vacío.
            </p>
          </div>
        )}

        {data != null && data.lineas_archivo > 0 && (
          <>
            {/* Las tres cifras, separadas a propósito: lo que se paga NO es el
                costo del empleador. */}
            <div className="grid grid-cols-3 gap-3">
              <Cifra titulo="Descontado al trabajador" valor={money(data.aporte_trabajador)} />
              <Cifra titulo="Aporte del empleador" valor={money(data.aporte_empleador)} />
              <Cifra titulo={data.declarado ? 'Total declarado en Previred' : 'Total a pagar en Previred'}
                valor={money(data.total)} destacado />
            </div>

            <p className="text-xs text-content-subtle">
              {data.trabajadores} trabajador{data.trabajadores === 1 ? '' : 'es'}
              {data.lineas_archivo !== data.trabajadores && (
                <>
                  {' '}en <strong>{data.lineas_archivo} líneas</strong> — hay más
                  líneas que personas porque el mes tuvo movimientos de personal
                  (licencias o cambios de contrato), que se declaran por separado.
                </>
              )}
            </p>

            <DataTable<typeof data.instituciones[number]>
              columns={[
                { key: 'nombre', header: 'Institución',
                  render: i => (
                    <>
                      {/* El NOMBRE va primero: «AFP · código 33» no le dice a
                          nadie a qué AFP hay que pagarle. El código se conserva
                          porque es lo que viaja en el archivo. */}
                      <div className="font-medium text-content">{i.nombre || i.etiqueta}</div>
                      {/* La segunda línea solo aparece si aporta algo: cuando no
                          hay nombre propio —la AFC es una sola— repetir la
                          etiqueta debajo no informa nada. */}
                      {(i.nombre !== '' || i.codigo !== '') && (
                        <div className="text-xs text-content-subtle">
                          {i.nombre !== '' && i.etiqueta}
                          {i.codigo !== '' && (
                            <>{i.nombre !== '' && ' · '}código {i.codigo}</>
                          )}
                        </div>
                      )}
                      <ul className="mt-1 space-y-0.5">
                        {i.conceptos.map(c => (
                          <li key={c.etiqueta} className="text-xs text-content-subtle flex gap-2">
                            <span className="flex-1">{c.etiqueta}</span>
                            <span className="tabular-nums">{money(c.monto)}</span>
                            <span className="w-20 shrink-0">
                              {c.quien_aporta === 'empleador' ? 'empleador' : 'trabajador'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) },
                { key: 'trabajadores', header: 'Trabajadores', numeric: true },
                { key: 'renta_imponible', header: 'Renta imponible', numeric: true,
                  render: i => <span className="text-content-subtle">{money(i.renta_imponible)}</span> },
                { key: 'total', header: 'A pagar', numeric: true,
                  render: i => <span className="font-medium">{money(i.total)}</span> },
              ]}
              rows={data.instituciones}
              rowKey={i => `${i.grupo}-${i.codigo}`}
              minWidth={640}
              surface={false}
              // Solo el total general: las columnas de trabajadores y renta
              // imponible no se suman entre instituciones —una persona aparece
              // en varias— y un total ahí sería un número que no significa nada.
              footer={{ total: money(data.total) }}
              footerLabel="Total"
              caption="Cotizaciones a pagar por institución"
            />
          </>
        )}
      </div>
    </Modal>
  )
}

const TEXTO_ORIGEN: Record<Declarado['origen'], string> = {
  clobi: 'Se guardó al marcar el período como pagado.',
  archivo: 'Se cargó desde el archivo que se subió a Previred.',
  reconstruido: 'La calculó Clobi después de pagado el período: si ese mes se pagó con otro '
    + 'sistema, puede no coincidir con lo que se pagó.',
}

/** El mes está cerrado: lo que se ve y se descarga es lo declarado. */
function AvisoDeclarado({ declarado }: { declarado: Declarado }) {
  const fecha = new Date(declarado.fecha).toLocaleDateString('es-CL')
  return (
    <div className="flex gap-2 items-start p-3 rounded-lg bg-glass-3">
      <Lock size={16} className="text-content-muted mt-0.5 shrink-0" />
      <div className="text-sm text-content">
        <p>
          <strong>Planilla declarada</strong>, guardada el {fecha}
          {declarado.por && <> por {declarado.por}</>}. Lo que se muestra y se descarga es lo
          declarado: los cambios posteriores en las fichas no la modifican.
        </p>
        <p className="text-xs text-content-subtle mt-0.5">{TEXTO_ORIGEN[declarado.origen]}</p>
      </div>
    </div>
  )
}

/**
 * Hoy el mes daría otra cifra. Se avisa con el detalle y NO se corrige solo:
 * una diferencia real se regulariza con una planilla complementaria en Previred.
 */
function AvisoDiferencia({ dif, money }: { dif: DiferenciaActual; money: (v: number) => string }) {
  const signo = (v: number) => (v > 0 ? `+${money(v)}` : money(v))
  return (
    <div className="flex gap-2 items-start p-3 rounded-lg bg-warning-surface">
      <AlertTriangle size={16} className="text-warning-content-soft mt-0.5 shrink-0" />
      <div className="text-sm text-content space-y-1 min-w-0">
        <p>
          Si este mes se generara hoy, el total cambiaría en <strong>{signo(dif.total)}</strong>
          {' '}(trabajador {signo(dif.aporte_trabajador)}, empleador {signo(dif.aporte_empleador)}).
          La cifra declarada no se modifica: si la diferencia es real, se regulariza con una
          planilla complementaria en Previred.
        </p>
        {dif.detalle.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-content-muted">
              Ver las {dif.detalle.length} diferencia{dif.detalle.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-1 space-y-0.5 text-xs text-content-subtle">
              {dif.detalle.map(d => (
                <li key={`${d.rut}-${d.linea}-${d.campo}`}>
                  <span className="text-content">{d.nombre || d.rut}</span>
                  {d.linea > 1 && <> (línea {d.linea})</>} · {d.etiqueta}:{' '}
                  <span className="tabular-nums">{d.declarado}</span> →{' '}
                  <span className="tabular-nums text-content">{d.actual}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  )
}

function Cifra({ titulo, valor, destacado = false }: {
  titulo: string
  valor: string
  destacado?: boolean
}) {
  return (
    <div className={`rounded-lg p-3 ${destacado ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-glass-3'}`}>
      <p className="text-xs text-content-muted">{titulo}</p>
      <p className={`text-lg font-bold tabular-nums ${destacado ? 'text-primary' : 'text-content'}`}>
        {valor}
      </p>
    </div>
  )
}
